/**
 * Request/response schemas for the patient-portal secure-messaging surface (`BACKLOG.md` W2, pillar
 * 3, `docs/decisions/patient-portal-messaging-scope.md`). Same reasoning as `schedulingRequests.ts`
 * for living in `shared/` rather than beside the routes: the server validates against these at the
 * boundary, both the patient-portal and staff route files share them, and a future web UI infers its
 * own types from the same objects.
 *
 * Two thread kinds, decided in full by the decision doc — restated here only as the closed enum this
 * file is responsible for, not re-derived: `care_team` (visible to any staff with an active
 * `careTeamMemberships` relationship to the patient) and `general` (one shared org-scoped unassigned
 * queue, claimable by any staff holding `patient:read`). `category` is a **display-only** field the
 * patient picks for a `general` thread — it drives no routing (the decision doc's own "routing
 * decision" section is explicit that this codebase has no department/on-call model to route
 * against yet).
 *
 * No server-owned value appears in any patient-facing request schema here: no `orgId`, no
 * `patientId` (always the portal session's own identity), no `status` on create (every thread starts
 * `open`), no `assignedToUserId` (server-decided, and `care_team` threads never have one at all).
 */

import { z } from 'zod';

export const MESSAGE_THREAD_KINDS = ['care_team', 'general'] as const;
export type MessageThreadKind = (typeof MESSAGE_THREAD_KINDS)[number];

export function isMessageThreadKind(value: string): value is MessageThreadKind {
  return (MESSAGE_THREAD_KINDS as readonly string[]).includes(value);
}

export const MESSAGE_THREAD_STATUSES = ['open', 'closed'] as const;
export type MessageThreadStatus = (typeof MESSAGE_THREAD_STATUSES)[number];

export function isMessageThreadStatus(value: string): value is MessageThreadStatus {
  return (MESSAGE_THREAD_STATUSES as readonly string[]).includes(value);
}

/** Display-only, `general` threads only — see this file's own header. Closed rather than
 * open-but-checked text: a category drives a staff filter UI, which needs a fixed, exhaustively
 * renderable set rather than an extensible one. */
export const MESSAGE_THREAD_CATEGORIES = [
  'billing',
  'scheduling',
  'general_question',
  'other',
] as const;
export type MessageThreadCategory = (typeof MESSAGE_THREAD_CATEGORIES)[number];

export function isMessageThreadCategory(value: string): value is MessageThreadCategory {
  return (MESSAGE_THREAD_CATEGORIES as readonly string[]).includes(value);
}

export const MESSAGE_SENDER_ROLES = ['patient', 'staff'] as const;
export type MessageSenderRole = (typeof MESSAGE_SENDER_ROLES)[number];

export function isMessageSenderRole(value: string): value is MessageSenderRole {
  return (MESSAGE_SENDER_ROLES as readonly string[]).includes(value);
}

export const MESSAGE_THREAD_READER_ROLES = ['patient', 'staff'] as const;
export type MessageThreadReaderRole = (typeof MESSAGE_THREAD_READER_ROLES)[number];

export const MAX_MESSAGE_SUBJECT_LENGTH = 200;
export const MAX_MESSAGE_BODY_LENGTH = 8000;

const subjectSchema = z.string().trim().min(1).max(MAX_MESSAGE_SUBJECT_LENGTH);
const messageBodySchema = z.string().trim().min(1).max(MAX_MESSAGE_BODY_LENGTH);

/**
 * Start a new thread — patient-portal only. `category` is required for `general` and forbidden for
 * `care_team` (which has no display-only routing field to carry, per this file's own header); both
 * directions are refined explicitly rather than left to silently ignore an extra/missing field.
 */
export const createMessageThreadRequestSchema = z
  .strictObject({
    kind: z.enum(MESSAGE_THREAD_KINDS),
    category: z.enum(MESSAGE_THREAD_CATEGORIES).optional(),
    subject: subjectSchema.optional(),
    body: messageBodySchema,
  })
  .refine((data) => data.kind !== 'general' || data.category !== undefined, {
    error: 'category is required for a general thread',
    path: ['category'],
  })
  .refine((data) => data.kind !== 'care_team' || data.category === undefined, {
    error: 'category only applies to a general thread',
    path: ['category'],
  });
export type CreateMessageThreadRequest = z.infer<typeof createMessageThreadRequestSchema>;

/** Reply into an existing thread — the same shape on both the patient-portal and staff surfaces;
 * which thread and who is replying come from the URL/session, never this body. */
export const sendMessageRequestSchema = z.strictObject({ body: messageBodySchema });
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

/**
 * The patient-portal detail-lookup query shape — `threadId` travels in the query string rather than
 * a `:threadId` path segment, deliberately: every patient-portal route path must stay static so
 * `app.ts`'s `denyByDefaultGate` can exempt it from the staff session gate by an exact string match
 * against `PUBLIC_API_PATHS` (`c.req.path` for a real request is the concrete path, never a route
 * *pattern*, so a `:threadId`-shaped registered path could never appear in that Set verbatim) — the
 * same constraint `patientAuth/chartRoutes.ts`'s own header states as "there is no `:patientId` path
 * parameter anywhere here," reached from the opposite direction: this surface has more than one
 * resource per patient, so an id has to travel somewhere, and a query/body field is the only shape
 * that keeps every registered path static. Ownership (does this `threadId` actually belong to the
 * caller's own `session.patientId`) is still enforced server-side on every route that accepts one —
 * this schema only shapes the request, it proves nothing about access.
 */
export const patientPortalThreadIdQuerySchema = z.strictObject({ threadId: z.uuid() });
export type PatientPortalThreadIdQuery = z.infer<typeof patientPortalThreadIdQuerySchema>;

export const patientPortalReplyRequestSchema = z.strictObject({
  threadId: z.uuid(),
  body: messageBodySchema,
});
export type PatientPortalReplyRequest = z.infer<typeof patientPortalReplyRequestSchema>;

/** The staff-side `:threadId` path parameter — dynamic segments are fine on this surface, since
 * every staff route runs behind the ordinary `requireSession`, not the exact-path-match exemption
 * mechanism the patient-portal schemas above exist to route around. */
export const messageThreadIdParamSchema = z.strictObject({ threadId: z.uuid() });

export const listMessageThreadsQuerySchema = z.strictObject({
  kind: z.enum(MESSAGE_THREAD_KINDS).optional(),
  status: z.enum(MESSAGE_THREAD_STATUSES).optional(),
  /** Staff-only: narrow the `general` queue to the caller's own claimed threads, or to the
   * unassigned pool. Meaningless (and ignored) on the patient-portal listing, which only ever
   * returns the caller's own threads regardless. */
  assigned: z.enum(['mine', 'unassigned']).optional(),
});
export type ListMessageThreadsQuery = z.infer<typeof listMessageThreadsQuerySchema>;

/** One thread, as returned to a client. `createdAt`/`updatedAt` are plain `z.string()`, matching
 * `appointmentSchema`'s own reasoning — round-tripping whatever Postgres's driver returned. */
export const messageThreadSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  patientId: z.uuid(),
  kind: z.enum(MESSAGE_THREAD_KINDS),
  category: z.enum(MESSAGE_THREAD_CATEGORIES).nullable(),
  subject: z.string().nullable(),
  status: z.enum(MESSAGE_THREAD_STATUSES),
  assignedToUserId: z.uuid().nullable(),
  /** Plain `z.array(z.string())`, matching `problemResponseSchema`'s own convention rather than
   * `appointmentSchema`'s enum-typed version — this surface has no route that lets a caller set a
   * restriction reason yet (every thread is created with an empty array; a future staff-side
   * labeling route would validate against `RESTRICTION_REASONS` at that write boundary, same as
   * every other clinical create route does). */
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MessageThread = z.infer<typeof messageThreadSchema>;

export const messageSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  threadId: z.uuid(),
  senderRole: z.enum(MESSAGE_SENDER_ROLES),
  authoredByUserId: z.uuid().nullable(),
  body: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Message = z.infer<typeof messageSchema>;

export const messageThreadResponseSchema = z.strictObject({ thread: messageThreadSchema });
export type MessageThreadResponse = z.infer<typeof messageThreadResponseSchema>;

export const messageThreadListResponseSchema = z.strictObject({
  threads: z.array(messageThreadSchema),
});
export type MessageThreadListResponse = z.infer<typeof messageThreadListResponseSchema>;

export const messageThreadDetailResponseSchema = z.strictObject({
  thread: messageThreadSchema,
  messages: z.array(messageSchema),
});
export type MessageThreadDetailResponse = z.infer<typeof messageThreadDetailResponseSchema>;

export const messageResponseSchema = z.strictObject({ message: messageSchema });
export type MessageResponse = z.infer<typeof messageResponseSchema>;

/** The failure vocabulary of this surface. Closed, matching `SchedulingErrorCode`'s own shape. */
export const MESSAGING_ERROR_CODES = [
  'invalid_request',
  'not_found',
  /** A staff reply/claim/close refused by `evaluateStaffThreadAccess` — no active care-team
   * relationship for a `care_team` thread, or a confidentiality label the caller isn't permitted. */
  'thread_access_denied',
  /** A `care_team` create with no active care-team relationship for the caller — a distinct, earlier
   * failure than `thread_access_denied` above, since no thread row exists yet to deny access *to*.
   * `docs/decisions/patient-portal-messaging-scope.md`'s own "A patient with no active care-team
   * relationship has no care-team thread to send to." */
  'no_care_team_relationship',
  /** The optimistic claim (`UPDATE ... WHERE assigned_to_user_id IS NULL`) lost the race — another
   * staff member claimed the same thread first. */
  'thread_already_claimed',
  /** `POST .../claim` against a `care_team` thread, which has no single owner by design. */
  'thread_not_claimable',
  'audit_unavailable',
] as const;
export type MessagingErrorCode = (typeof MESSAGING_ERROR_CODES)[number];

export function isMessagingErrorCode(value: string): value is MessagingErrorCode {
  return (MESSAGING_ERROR_CODES as readonly string[]).includes(value);
}

export const messagingErrorResponseSchema = z.strictObject({
  error: z.enum(MESSAGING_ERROR_CODES),
  denialReason: z.enum(['noCareTeamRelationship', 'confidentialityLabel']).optional(),
});
export type MessagingErrorResponse = z.infer<typeof messagingErrorResponseSchema>;
